import { isAction, Middleware, Reducer } from "@reduxjs/toolkit";

type RegisteredSlice = {
  reducer: Reducer;
  actions: Record<string, unknown>;
};
type RegisteredSlices = Record<string, RegisteredSlice>;

const hasStringType = (value: unknown): value is { type: string } =>
  (typeof value === "object" || typeof value === "function") &&
  value !== null &&
  "type" in value &&
  typeof value.type === "string";

/**
 * Builds the reducer map of a store from its registered slices, keeping the state type of every slice.
 * Using this together with `createUnhandledActionMiddleware` guarantees that both see the same slices.
 */
export const getSliceReducers = <Slices extends RegisteredSlices>(
  slices: Slices
): { [Key in keyof Slices]: Slices[Key]["reducer"] } =>
  Object.fromEntries(Object.entries(slices).map(([key, slice]) => [key, slice.reducer])) as {
    [Key in keyof Slices]: Slices[Key]["reducer"];
  };

/**
 * Collects the types of all RTK action creators of the registered slices.
 * Plain helper functions without a `type` are skipped.
 */
export const getKnownActionTypes = (slices: RegisteredSlices): Set<string> =>
  new Set(
    Object.values(slices)
      .flatMap(slice => Object.values(slice.actions))
      .filter(hasStringType)
      .map(({ type }) => type)
  );

/**
 * Development middleware which warns once per action type if an action is dispatched, which none
 * of the registered slices can handle. Without it Redux silently returns the current state.
 *
 * Pass the same slices as the store's reducers are built from (see `getSliceReducers`), so a slice
 * which is not registered in the store has no known actions and its actions are reported.
 *
 * A reducer's default case cannot be used for this: every slice receives every action, so
 * "not handled by this slice" is the normal case.
 *
 * Add it to the store only in non-production builds.
 */
export const createUnhandledActionMiddleware = (
  slices: RegisteredSlices,
  warn: (message: string) => void = message => console.warn(message)
): Middleware => {
  const knownTypes = getKnownActionTypes(slices);
  const reportedTypes = new Set<string>();

  return () => next => action => {
    if (isAction(action) && !knownTypes.has(action.type) && !reportedTypes.has(action.type)) {
      reportedTypes.add(action.type);
      warn(
        `Redux: no reducer handles the action "${action.type}". ` +
          "Is its slice missing in the store's registered slices, or is the action type misspelled?"
      );
    }

    return next(action);
  };
};
